import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useChat } from '@ai-sdk/react';
import {
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithToolCalls,
} from 'ai';
import { itemsApi } from '@/api';
import { aiAuthHeaders, emailBuilderChatUrl } from '@/api/ai/ai.service';
import type {
  ApplyEmailEditsInput,
  ApplyEmailEditsOutput,
  EmailBuilderUIMessage,
} from '@/api/ai/ai.types';
import type { Item } from '@/api/items/items.types.ts';
import type { RenderEmailRequest } from '@/api/email-builder/email-builder.types.ts';
import { applyEmailEdits } from '../ai-edit-operations';

/** The four lifecycle states the AI SDK reports for a chat. */
export type ChatStatus = 'submitted' | 'streaming' | 'ready' | 'error';

export interface UseEmailBuilderChat {
  messages: EmailBuilderUIMessage[];
  status: ChatStatus;
  error: Error | undefined;
  /** True while a request is in flight (submitted or streaming). */
  isBusy: boolean;
  /** Sends a user message; ignored while busy or when `text` is blank. */
  send: (text: string) => void;
  /** Aborts the in-flight generation, keeping the tokens received so far. */
  stop: () => void;
  /** Regenerates the last assistant turn after an error. */
  retry: () => void;
  /** Clears the conversation. */
  reset: () => void;
}

interface UseEmailBuilderChatArgs {
  /** Current builder draft — re-sent with every request as agent context. */
  draft: RenderEmailRequest;
  /** Applies the AI's resolved edits to the live builder state. */
  onApplyEdits: (draft: RenderEmailRequest) => void;
}

/** True when a batch touches the catalog and therefore needs item data. */
function needsCatalog(input: ApplyEmailEditsInput): boolean {
  return input.operations.some(
    (op) => op.op === 'add-product' || op.op === 'add-product-list',
  );
}

/**
 * Drives the email-builder AI assistant: a real streaming `useChat`
 * conversation against `POST /v1/ai/email-builder/chat`.
 *
 * The agent's `applyEmailEdits` tool is **client-executed** — the backend only
 * declares it. When the model calls it, `onToolCall` resolves any product ids
 * against the catalog, applies the operations to the draft, pushes the result
 * into the builder, and returns a compact tool output. `sendAutomaticallyWhen`
 * then resumes the turn so the agent can write its closing reply.
 */
export function useEmailBuilderChat({
  draft,
  onApplyEdits,
}: UseEmailBuilderChatArgs): UseEmailBuilderChat {
  const queryClient = useQueryClient();

  // Keep the latest draft + callback reachable from the transport / tool
  // closures, which are created once and must always see current values.
  const draftRef = useRef(draft);
  const onApplyEditsRef = useRef(onApplyEdits);
  useEffect(() => {
    draftRef.current = draft;
    onApplyEditsRef.current = onApplyEdits;
  });

  const transport = useMemo(
    () =>
      // `prepareSendMessagesRequest` runs per network request, never during
      // render — reading the draft ref there is the correct way to re-send
      // the live draft on every turn (incl. the automatic tool-call resume).
      // eslint-disable-next-line react-hooks/refs -- deferred transport callback
      new DefaultChatTransport<EmailBuilderUIMessage>({
        api: emailBuilderChatUrl(),
        prepareSendMessagesRequest: ({ messages }) => ({
          headers: aiAuthHeaders(),
          body: { messages, draft: draftRef.current },
        }),
      }),
    [],
  );

  const chat = useChat<EmailBuilderUIMessage>({
    transport,
    // Resume the turn once the client tool result is in.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    async onToolCall({ toolCall }) {
      // Catalog tools (searchItems / getItem) run on the backend; only the
      // client-executed applyEmailEdits reaches here.
      if (toolCall.dynamic || toolCall.toolName !== 'applyEmailEdits') return;

      const input = toolCall.input;
      try {
        let items: Item[] = [];
        if (needsCatalog(input)) {
          items = await queryClient.ensureQueryData<Item[]>({
            queryKey: ['items', undefined],
            queryFn: () => itemsApi.getAllItems().then((res) => res.data),
          });
        }
        const byId = new Map(items.map((item) => [item.id, item]));
        const byLowerId = new Map(
          items.map((item) => [item.id.toLowerCase(), item]),
        );
        const resolveItem = (itemId: string) =>
          byId.get(itemId) ?? byLowerId.get(itemId.toLowerCase());

        const { draft: nextDraft, warnings } = applyEmailEdits(
          draftRef.current,
          input.operations,
          resolveItem,
        );
        onApplyEditsRef.current(nextDraft);

        const output: ApplyEmailEditsOutput = {
          ok: true,
          summary: input.summary,
          blockCount: nextDraft.blocks.length,
        };
        if (warnings.length > 0) output.warnings = warnings;

        // Not awaited — see AI SDK guidance on avoiding tool-call deadlocks.
        chat.addToolOutput({
          tool: 'applyEmailEdits',
          toolCallId: toolCall.toolCallId,
          output,
        });
      } catch (err) {
        chat.addToolOutput({
          tool: 'applyEmailEdits',
          toolCallId: toolCall.toolCallId,
          state: 'output-error',
          errorText:
            err instanceof Error
              ? err.message
              : 'Die Änderungen konnten nicht übernommen werden.',
        });
      }
    },
  });

  const { messages, status, error, sendMessage, stop, regenerate, setMessages } =
    chat;

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || status === 'submitted' || status === 'streaming') return;
      void sendMessage({ text: trimmed });
    },
    [sendMessage, status],
  );

  const retry = useCallback(() => {
    void regenerate();
  }, [regenerate]);

  const reset = useCallback(() => {
    stop();
    setMessages([]);
  }, [stop, setMessages]);

  return {
    messages,
    status,
    error,
    isBusy: status === 'submitted' || status === 'streaming',
    send,
    stop,
    retry,
    reset,
  };
}
